import { animated, useSpring } from '@react-spring/three'
import { animated as animatedWeb, useSpring as useSpringWeb } from '@react-spring/web'
import { CameraControls, RoundedBoxGeometry, ScreenSizer, useCursor } from '@react-three/drei'
import { Canvas, useThree, type ThreeElements } from '@react-three/fiber'
import { useEffect, useState } from 'react'
import { MathUtils } from 'three'

import { GamePhase, useGameStore } from '@stores'

const BUTTON_WIDTH = 100
const BUTTON_HEIGHT = 50
const BUTTON_DEPTH = 100
const BUTTON_SCALE = 0.5
const BUTTON_GAP = 10

type ButtonProps = ThreeElements['mesh'] & {
  index: number
}

function Button(props: ButtonProps) {
  const [clicked, setClicked] = useState(false)
  const [hovered, setHovered] = useState(false)
  useCursor(hovered)

  const { positionY } = useSpring({ positionY: clicked ? -(BUTTON_HEIGHT * BUTTON_SCALE) / 2 : 0 })

  return (
    <animated.mesh
      scale={BUTTON_SCALE}
      position-x={(BUTTON_WIDTH * BUTTON_SCALE + BUTTON_GAP) * (props.index - 0.5)}
      position-y={positionY}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
      onPointerDown={() => setClicked(true)}
      onPointerUp={() => setClicked(false)}
      {...props}
    >
      <RoundedBoxGeometry args={[BUTTON_WIDTH, BUTTON_HEIGHT, BUTTON_DEPTH]} />
      <meshMatcapMaterial />
    </animated.mesh>
  )
}

function DashboardScene() {
  const { controls } = useThree()

  useEffect(() => {
    if (!controls) return
    const cc = controls as CameraControls
    // cc.rotateAzimuthTo(MathUtils.degToRad(5))
    cc.rotatePolarTo(MathUtils.degToRad(45))
  }, [controls])

  const buttons = [
    { label: 'Play', onClick: () => console.log('Play') },
    { label: 'Settings', onClick: () => console.log('Settings') },
  ]

  return (
    <>
      <CameraControls makeDefault enabled={false} />
      <mesh
        scale={[
          BUTTON_WIDTH * BUTTON_SCALE * buttons.length + BUTTON_GAP * buttons.length * 2,
          BUTTON_HEIGHT * BUTTON_SCALE,
          BUTTON_DEPTH,
        ]}
        position-y={(-BUTTON_HEIGHT * BUTTON_SCALE) / buttons.length - BUTTON_GAP / buttons.length}
      >
        <RoundedBoxGeometry />
        <meshMatcapMaterial color="brown" />
      </mesh>

      {buttons.map((_, index) => (
        <Button key={index} index={index} />
      ))}
    </>
  )
}

const AnimatedCanvas = animatedWeb(Canvas)

export function Dashboard() {
  const phase = useGameStore(state => state.phase)
  const spring = useSpringWeb({ opacity: phase === GamePhase.PLAYING ? 1 : 0 })

  return (
    <AnimatedCanvas
      shadows
      orthographic
      camera={{ zoom: 100, near: -1000, far: 1000 }}
      style={{
        position: 'fixed',
        zIndex: 999,
        bottom: 0,
        right: 0,
        width: 200,
        height: 100,
        ...spring,
      }}
    >
      <ScreenSizer scale={1}>
        <DashboardScene />
      </ScreenSizer>
    </AnimatedCanvas>
  )
}
